import { useQuery } from "@apollo/client/react";
import { Button, ButtonText } from "@repo/components/src/ui/button";
import { HStack } from "@repo/components/src/ui/hstack";
import { Icon } from "@repo/components/src/ui/icon";
import { Text } from "@repo/components/src/ui/text";
import { VStack } from "@repo/components/src/ui/vstack";
import { IDENTIFIER_HANDLERS } from "@repo/components/src/utils/identifier/handlers";
import { TaxIdRegistryMatchDocument } from "@repo/graphql/documents/fiscal/query.generated";
import { IdentifierTypeEnum } from "@repo/graphql/gql/graphql";
import { CheckCheck } from "lucide-react-native";
import { useEffect } from "react";
import { UseFormReturn } from "react-hook-form";

import type { RegistryMatchState } from "./useRegistryMatchState";
import type { TaxProfileFormValues } from "./types";

type Props = {
  formProps: UseFormReturn<TaxProfileFormValues>;
  registryMatch: RegistryMatchState;
  /** The tax IDs the identifier field accepts, the country's own first. */
  identifierTypes: ReadonlyArray<IdentifierTypeEnum>;
  hasBusinessActivityField: boolean;
};

// Offers to prefill legalName/businessActivity from the tax authority's
// registry once the tax ID the creator typed is valid. Never overwrites a
// value the creator typed themselves — it only offers while the current
// legalName is empty, the profile's default, or a value this card itself
// filled in earlier for the same tax ID.
//
// `registryMatch` is owned by the form rather than local state, because
// this card unmounts when the form swaps to the review step — local state
// would forget the creator's choice on the edit → review → edit round trip.
export default function RegistryMatchCard({
  formProps,
  registryMatch,
  identifierTypes,
  hasBusinessActivityField,
}: Props) {
  const {
    defaultLegalName,
    dismissedForTaxId,
    onDismiss,
    acceptedLegalName,
    onAccept,
  } = registryMatch;
  const taxIdField = formProps.watch("taxId");
  const taxId = taxIdField?.value ?? "";
  const legalName = formProps.watch("legalName");

  // A number saved without a type is the country's own tax ID.
  const taxIdType = taxIdField?.type ?? identifierTypes[0];
  const isValidTaxId =
    !!taxIdType && IDENTIFIER_HANDLERS[taxIdType].validate(taxId);

  const { data } = useQuery(TaxIdRegistryMatchDocument, {
    variables: { taxId },
    skip: !isValidTaxId,
  });

  const match = data?.fiscal.taxIdRegistryMatch ?? null;
  const nameMatchesRegistry = !!match && legalName === match.legalName;

  // The giro follows the name: whenever the field holds the registry name
  // and the form asks for a giro, fill it from the registry if the creator
  // hasn't typed one. Covers the tap, a name that already matched (no tap
  // ever happens), and switching to a regime that asks for a giro after the
  // name was confirmed. Guarded on an empty field, so it never overwrites a
  // typed giro and never loops.
  useEffect(() => {
    if (!match || !nameMatchesRegistry || !hasBusinessActivityField) {
      return;
    }
    if (!match.businessActivity || formProps.getValues("businessActivity")) {
      return;
    }
    formProps.setValue("businessActivity", match.businessActivity, {
      shouldValidate: true,
    });
  }, [match, nameMatchesRegistry, hasBusinessActivityField, formProps]);

  if (!match || dismissedForTaxId === taxId) {
    return null;
  }

  // Once the field holds the registry name -- because the creator accepted
  // the suggestion, or because it already matched what they had -- asking
  // "¿Eres X?" again reads as a dead button. Show the match as confirmed.
  if (nameMatchesRegistry) {
    return (
      <HStack
        space="sm"
        className="items-center rounded-2xl border border-success-100 bg-success-50 p-3"
        accessibilityLiveRegion="polite"
      >
        <Icon as={CheckCheck} size="sm" className="text-success-700" />
        <Text size="sm" className="flex-1 text-typography-700">
          Datos confirmados con el registro tributario.
        </Text>
      </HStack>
    );
  }

  const canOfferMatch =
    legalName === "" ||
    legalName === defaultLegalName ||
    legalName === acceptedLegalName;
  if (!canOfferMatch) {
    return null;
  }

  return (
    <VStack
      space="sm"
      className="rounded-2xl border border-brand-100 bg-brand-50 p-4"
    >
      <Text bold className="text-typography-950 text-[15px]">
        {`¿Eres ${match.legalName}?`}
      </Text>
      <HStack space="sm">
        <Button
          action="brand"
          variant="solid"
          size="sm"
          className="rounded-full flex-1"
          onPress={() => {
            formProps.setValue("legalName", match.legalName, {
              shouldValidate: true,
            });
            onAccept(match.legalName);
          }}
        >
          <ButtonText>Sí, soy yo</ButtonText>
        </Button>
        <Button
          action="secondary"
          variant="outline"
          size="sm"
          className="rounded-full flex-1"
          onPress={() => onDismiss(taxId)}
        >
          <ButtonText>No, ingresar manualmente</ButtonText>
        </Button>
      </HStack>
    </VStack>
  );
}
